<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MeterRelations',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    meter: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const relationItemToPayload = (...args) => {
    const p = args[0];
    const meter = props.meter;
    if (p.relation_type === 'sub') {
      p.parent_meter_id = meter.id;
      p.child_meter_id = p.related_meter.id;
    } else if (p.relation_type === 'data') {
      p.parent_meter_id = p.related_meter.id;
      p.child_meter_id = meter.id;
    } else if (p.relation_type === 'parent') {
      p.parent_meter_id = p.related_meter.id;
      p.child_meter_id = meter.id;
    }
    return p;
  };
  const mapRelationItem = (...args) => {
    const p = args[0];

    if (!p) {
      return p;
    }

    const meter = props.meter;

    // the current meter is the parent, the other one is the child
    const isRelatedChild = p.child_meter_id === meter.id;

    if (isRelatedChild) {
      p.related_meter = p.parent_meter;
      p.relation_type =
        p.related_meter.meter_type == 'pulse' ? 'data' : 'parent';
      return p;
    }

    p.related_meter = p.child_meter;
    p.relation_type = 'sub';
    return p;
  };

  const relationTypes = ref([
    {
      id: 'parent',
      text: $fn.value.pgettext('MeterRelationTypes', 'Parent'),
    },
    {
      id: 'sub',
      text: $fn.value.pgettext('MeterRelationTypes', 'Submeter'),
    },
    {
      id: 'data',
      text: $fn.value.pgettext('MeterRelationTypes', 'Data Provider'),
    },
  ]);
  const relationTypesMap = ref(null);
  const items = ref(null);
  const ux = ref({
    errorHandling: 'local',
    initialLoadSpinner: true,
  });
  const b_2248226175642056_columns = computed(() => [
    {
      templateRef: 'DTColumnInactive',
    },
    {
      name: 'relation_type',
      type: 'select',
      header: $fn.value.gettext('Relation type'),
      width: '170px',
      options: relationTypes.value,
    },
    {
      name: 'related_meter',
      type: 'select2',
      header: $fn.value.gettext('Device'),
      width: '270px',
      formatter: {
        name: 'template',
        value:
          '<div v-if="value" class="text-start">\n\
  <ItpLink :to="{ name: \'app.meter\', params: row.related_meter}">\n\
    {{ value.serial_number }}\n\
  </ItpLink>\n\
  <div v-if="value.deveui">{{ value.deveui }}</div>\n\
  <div v-if="value.tag_id">{{ value.tag_id }}</div>\n\
  <div v-if="value.description">{{ value.description }}</div>\n\
</div>\n\
',
      },
      options: $fn.value.searchDataSource('suggest_related_meter', {
        // relation_type: row.relation_type,
        parent: props.meter.id,
      }),
      optionValueField: 'id',
      valueField: 'id',
      textField: 'serial_number',
      bindObject: true,
      optionTemplate:
        '<div class="text-start">\n\
  {{ option.serial_number }}\n\
  <div v-if="option.deveui">{{ option.deveui }}</div>\n\
  <div v-if="option.tag_id">{{ option.tag_id }}</div>\n\
  <div v-if="option.description">{{ option.description }}</div>\n\
</div>\n\
',
      validator: ['required'],
    },
    {
      name: 'slot_number',
      type: 'number',
      header: $fn.value.gettext('Slot Number'),
      width: '140px',
    },
    {
      name: 'offset',
      type: 'number',
      header: $fn.value.gettext('Offset'),
      width: '140px',
      required: true,
    },
    {
      name: 'notes',
      type: 'textarea',
      width: '200px',
      header: $fn.value.gettext('Notes'),
    },
  ]);

  const $data = computed(() => ({
    relationTypes: relationTypes.value,
    relationTypesMap: relationTypesMap.value,
    items: items.value,
    ux: ux.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_relationTypesMap = async () => {
    return _.zipObject(
      _.map(relationTypes.value, (p) => p.id),
      _.map(relationTypes.value, (p) => p.text)
    );
  };
  const $$load_items = async () => {
    return $fn.value
      .fetch('list_meter_relationship', {
        parameters: { meter_id: props.meter.id },
      })
      .then((items) => items.map((p) => mapRelationItem(p)));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'relationTypes' },
      { name: 'relationTypesMap' },
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { relationTypesMap: $$load_relationTypesMap, items: $$load_items },
    ux: { errorHandling: 'local', initialLoadSpinner: true },
    dataRefs: {
      relationTypes,
      relationTypesMap,
      items,
      ux,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const act_2248226175642056_script_woe0phykq_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value
        .crud('meter_relationship')
        .upsert(relationItemToPayload($event.data.row))
        .then((p) => mapRelationItem(p))
    );
  };
  const act_2248226175642056_script_woe0phykq_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_woe0phykq_value_callable($event);
      },
    };
  };
  const act_2248226175642056_script_vtipa6qug_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('meter_relationship').delete($event.data.row)
    );
  };
  const act_2248226175642056_script_vtipa6qug_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_vtipa6qug_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_lxvq3ho9u =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_fn759sbwk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_lxvq3ho9u,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_fn759sbwk],
    event: 'reload',
  });
  const act_2248226175642056_script_woe0phykq = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_woe0phykq_getActionArgs,
  });
  const inst_evh_2248226175642056_save_t4lktbjdk =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_woe0phykq,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_2248226175642056_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_save_t4lktbjdk],
    event: 'save',
  });
  const act_2248226175642056_script_vtipa6qug = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_vtipa6qug_getActionArgs,
  });
  const inst_evh_2248226175642056_delete_76dzpk766 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_vtipa6qug,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_2248226175642056_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_delete_76dzpk766],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_reload_fn759sbwk,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_save_t4lktbjdk,
    inst_evh_2248226175642056_save,
    inst_evh_2248226175642056_delete_76dzpk766,
    inst_evh_2248226175642056_delete,
  };

  const evh_7315092382398562_reload_fn759sbwk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_fn759sbwk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_save_t4lktbjdk = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_save_t4lktbjdk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_save = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_delete_76dzpk766 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_delete_76dzpk766.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_delete = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    loading-spinner
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataTable
      tableId="MeterRelations"
      editable
      :value="_.orderBy(items, ['created_at'], ['desc'])"
      :rowTemplate="{ meter_id: meter.id, inactive: false, factor: 0 }"
      :columns="b_2248226175642056_columns"
      v-if="loaded"
      @save="evh_2248226175642056_save($event, {})"
      @delete="evh_2248226175642056_delete($event, {})"
    >
    </ItpDataTable>
  </ItpUsercontrol>
</template>
